import config from './config';

export interface DomainBrand {
  id: number;
  domain: string;
  verified: boolean;
  sslEnabled: boolean;
  certificateProvisioned?: boolean;
  certificateIssuedAt?: string;
  certificateExpiresAt?: string;
  certificateError?: string;
  uploadedAt: string;
  updatedAt?: string;
}

/**
 * Get all domain brands for the authenticated user
 * @param token - Authentication token (required)
 * @returns Array of domain brands
 */
export const getDomainBrands = async (
  token: string
): Promise<DomainBrand[]> => {
  const apiUrl = `${config.brandServiceUrl}/api/domain/brands`;
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed. Please sign in again.');
    }
    throw new Error('Failed to fetch domain brands');
  }

  return await response.json();
};

/**
 * Add a new domain brand
 * @param domain - The domain name to add
 * @param sslEnabled - Whether SSL should be enabled
 * @param token - Authentication token (required)
 * @returns The created domain brand
 */
export const addDomainBrand = async (
  domain: string,
  sslEnabled: boolean,
  token: string
): Promise<DomainBrand> => {
  const apiUrl = `${config.brandServiceUrl}/api/domain/brand`;
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      domain,
      sslEnabled,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to add domain brand');
  }

  return await response.json();
};

/**
 * Update an existing domain brand
 * @param domainId - The ID of the domain to update
 * @param domain - The domain name
 * @param sslEnabled - Whether SSL should be enabled
 * @param token - Authentication token (required)
 * @returns The updated domain brand
 */
export const updateDomainBrand = async (
  domainId: number,
  domain: string,
  sslEnabled: boolean,
  token: string
): Promise<DomainBrand> => {
  const apiUrl = `${config.brandServiceUrl}/api/domain/brand/${domainId}`;
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const response = await fetch(apiUrl, {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      domain,
      sslEnabled,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update domain brand');
  }

  return await response.json();
};

/**
 * Delete a domain brand
 * @param domainId - The ID of the domain to delete
 * @param token - Authentication token (required)
 * @returns Success message
 */
export const deleteDomainBrand = async (
  domainId: number,
  token: string
): Promise<{ message: string }> => {
  const apiUrl = `${config.brandServiceUrl}/api/domain/brand/${domainId}`;
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(apiUrl, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete domain brand');
  }

  // Handle empty response (204 No Content) or parse JSON
  const contentType = response.headers.get('content-type');
  if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
    return { message: 'Domain brand deleted successfully' };
  }

  return await response.json();
};
